/**
 * Parser for MarkdownWithComponents
 *
 * Pure functions that split raw markdown content into segments:
 * - Regular markdown text → rendered by MarkdownRenderer
 * - ```ui:<componentType> blocks → rendered by registered components
 *
 * Handles:
 * - Fenced code block detection with ```ui:<name> syntax
 * - Simple key-value prop parsing (snake_case → camelCase)
 * - Unclosed trailing blocks (suppressed for streaming support)
 */

import type { Segment } from './types'

// Match completed ```ui:<name>\n<props>``` blocks
const UI_BLOCK_REGEX = /```ui:(\w+)\n([\s\S]*?)```/g

// Detect an unclosed ```ui: block at the end of the content
const UNCLOSED_TRAILING_BLOCK_REGEX = /```ui:\w+[^`]*$/

/**
 * Convert a snake_case string to camelCase.
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

/**
 * Parse a raw key-value prop block into a Record<string, string>.
 * Each line should be `key: value`. Keys are converted to camelCase.
 * Values have surrounding quotes stripped if present.
 *
 * @example
 *   parseProps('yes_label: "Confirm"\nno_label: "Cancel"')
 *   // { yesLabel: 'Confirm', noLabel: 'Cancel' }
 */
export function parseProps(raw: string): Record<string, string> {
  const props: Record<string, string> = {}
  const lines = raw.split('\n')

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue

    // Split on first colon only
    const colonIndex = trimmed.indexOf(':')
    if (colonIndex === -1) continue

    const key = trimmed.slice(0, colonIndex).trim()
    let value = trimmed.slice(colonIndex + 1).trim()

    // Strip surrounding quotes (single or double)
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }

    props[snakeToCamel(key)] = value
  }

  return props
}

/**
 * Parse raw markdown content into an array of segments.
 *
 * Regular markdown text becomes `{ type: 'markdown', content }` segments.
 * ```ui:<name> blocks become `{ type: 'component', componentType, props }` segments.
 * Unclosed trailing ```ui: blocks are suppressed (streaming support).
 *
 * If no `ui:` blocks are present, returns a single markdown segment.
 */
export function parseSegments(content: string): Segment[] {
  // Suppress unclosed trailing ui: block (streaming partial content)
  let processedContent = content
  const unclosedMatch = UNCLOSED_TRAILING_BLOCK_REGEX.exec(processedContent)

  if (unclosedMatch) {
    // Verify this isn't actually a closed block by checking if the match
    // overlaps with any completed block
    const allClosedBlocks = [...processedContent.matchAll(UI_BLOCK_REGEX)]
    const unclosedStart = unclosedMatch.index

    const isInsideClosedBlock = allClosedBlocks.some(
      closedMatch =>
        closedMatch.index !== undefined &&
        unclosedStart >= closedMatch.index &&
        unclosedStart < closedMatch.index + closedMatch[0].length
    )

    if (!isInsideClosedBlock) {
      processedContent = processedContent.slice(0, unclosedStart)
    }
  }

  const segments: Segment[] = []
  let lastIndex = 0

  // Reset regex state
  UI_BLOCK_REGEX.lastIndex = 0

  let match: RegExpExecArray | null
  while ((match = UI_BLOCK_REGEX.exec(processedContent)) !== null) {
    // Add preceding markdown if any
    if (match.index > lastIndex) {
      const markdownContent = processedContent
        .slice(lastIndex, match.index)
        .trim()
      if (markdownContent) {
        segments.push({ type: 'markdown', content: markdownContent })
      }
    }

    // Add component segment
    segments.push({
      type: 'component',
      componentType: match[1],
      props: parseProps(match[2]),
    })

    lastIndex = match.index + match[0].length
  }

  // Add trailing markdown if any
  if (lastIndex < processedContent.length) {
    const trailing = processedContent.slice(lastIndex).trim()
    if (trailing) {
      segments.push({ type: 'markdown', content: trailing })
    }
  }

  // If nothing was parsed, return the full content as a single markdown segment
  if (segments.length === 0 && content.trim()) {
    segments.push({ type: 'markdown', content: content.trim() })
  }

  return segments
}
